import { ChevronDown, ChevronRight, UserRound } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { PERSONA_SORT_LABELS } from '../constants';
import type { PersonaReviewItem, PersonaSortOption } from '../types';
import { ScoreBadge } from './status';

export function PersonaQueue({
  personas,
  selectedPersonaId,
  sortBy,
  onSortChange,
  onSelectPersona,
}: {
  personas: PersonaReviewItem[];
  selectedPersonaId: string | null;
  sortBy: PersonaSortOption;
  onSortChange: (sortBy: PersonaSortOption) => void;
  onSelectPersona: (id: string) => void;
}) {
  return (
    <div className="flex min-h-0 flex-col border-r">
      <div className="flex min-h-[52px] items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <UserRound className="h-4 w-4 text-muted-foreground" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Personas</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
                {PERSONA_SORT_LABELS[sortBy]}
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Sort personas</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {(Object.keys(PERSONA_SORT_LABELS) as PersonaSortOption[]).map((option) => (
                <DropdownMenuItem
                  key={option}
                  className="cursor-pointer"
                  onClick={() => onSortChange(option)}
                >
                  {PERSONA_SORT_LABELS[option]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="rounded-md bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
            {personas.length}
          </span>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto divide-y">
        {personas.map((persona) => {
          const isSelected = persona.id === selectedPersonaId;
          const weakCount = persona.score?.weak.length ?? 0;
          return (
            <button
              key={persona.id}
              type="button"
              onClick={() => onSelectPersona(persona.id)}
              className={cn(
                'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                isSelected && 'bg-muted/50',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium text-foreground">{persona.label}</span>
                  {persona.score ? <ScoreBadge score={persona.score.average} prefix="" /> : null}
                </div>
                <p className="mt-1 truncate text-xs text-muted-foreground">
                  {[persona.title, persona.company, persona.population].filter(Boolean).join(' · ')}
                </p>
                {persona.goal ? (
                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">{persona.goal}</p>
                ) : null}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {weakCount > 0 ? (
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-xs font-normal text-amber-700">
                      {weakCount} weak {weakCount === 1 ? 'criterion' : 'criteria'}
                    </Badge>
                  ) : null}
                  <Badge variant="outline" className="border-border bg-background text-xs font-normal text-muted-foreground">
                    {persona.conversations.length} {persona.conversations.length === 1 ? 'conversation' : 'conversations'}
                  </Badge>
                </div>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
